import { saveUserSearch, createSearchItemFromElement, SearchEntryInterface } from "./search_history"

/**
 * Search Helper Keyboard Navigation
 *
 * Provides keyboard navigation for anchor elements in search interfaces.
 * Supports navigation between search results and default recommendations with highlighting.
 *
 * Key Features:
 * - Circular navigation (wraps from last to first element and vice versa)
 * - Arrow Up/Down keys for navigation with visual highlighting
 * - Enter key to select and navigate to highlighted item
 * - Escape key to clear current selection
 * - Automatic state reset when search input changes
 * - Type-safe TypeScript implementation
 *
 * Selecting an item navigates to its href.
 *
 * @param searchElement - The search container element (#universal-search)
 */
export const SearchHelperNavigation = (searchElement: HTMLElement) => {
  if (!searchElement) return

  const searchInput = searchElement.querySelector("input") as HTMLInputElement
  if (!searchInput) return

  let currentHighlightedIndex = -1

  const getNavigableElements = (): HTMLLIElement[] => {
    const inputValue = searchInput.value.trim()
    // :not(.hide) -- recommendations are server-rendered in full and trimmed by
    // hiding trailing tiles, which arrow keys would otherwise walk onto.
    const selector = inputValue ? ".search-preview li, a.search-navigation" : ".default-recommendations li:not(.hide)"
    return Array.from(searchElement.querySelectorAll(selector))
  }

  const setHighlight = (index: number) => {
    const elements = getNavigableElements()

    // Clear all highlights
    searchElement.querySelectorAll(".universal-search-helper .highlighted").forEach(element => element.classList.remove("highlighted"))

    // Set new highlight if valid index
    if (index >= 0 && index < elements.length) {
      elements[index].classList.add("highlighted")
      currentHighlightedIndex = index
    } else {
      currentHighlightedIndex = -1
    }
  }

  const navigate = (direction: "up" | "down") => {
    const elements = getNavigableElements()
    if (elements.length === 0) return

    let newIndex: number

    if (direction === "up") {
      newIndex = currentHighlightedIndex <= 0 ? elements.length - 1 : currentHighlightedIndex - 1
    } else {
      newIndex =
        currentHighlightedIndex === -1 || currentHighlightedIndex >= elements.length - 1
          ? 0
          : currentHighlightedIndex + 1
    }

    setHighlight(newIndex)
  }

  const selectHighlighted = () => {
    const elements = getNavigableElements()
    if (currentHighlightedIndex >= 0 && currentHighlightedIndex < elements.length) {
      const selectedElement = elements[currentHighlightedIndex]
      const anchor = selectedElement.querySelector("a") as HTMLAnchorElement

      if (anchor) {
        const term = anchor.dataset.term
        if (!term) return

        const searchEntry = createSearchItemFromElement(anchor) as SearchEntryInterface | null
        if (!searchEntry) return
        saveUserSearch(searchEntry)

        let eventScope = "recommended_service"
        let params = { service: searchEntry.term }
        if (searchEntry.type === "search") {
          eventScope = "recent_searches"
          params = { term: searchEntry.term }
        }

        if (anchor.href) {
          if (searchInput.value.trim()) {
            // It means that was a search result
            window.App.Utils.logCustomDatadogAction(`universal_search_service_filter_keyboard_enter`, params)
          } else {
            window.App.Utils.logCustomDatadogAction(`universal_search_${eventScope}_keyboard_enter`, params)
          }
          window.location.assign(anchor.href)
        }
      }
    }
  }

  const resetState = () => {
    currentHighlightedIndex = -1
    setHighlight(-1)
  }

  searchInput.addEventListener("input", resetState)

  searchInput.addEventListener("keydown", (event: KeyboardEvent) => {
    switch (event.key) {
      case "ArrowUp":
        event.preventDefault()
        navigate("up")
        break

      case "ArrowDown":
        event.preventDefault()
        navigate("down")
        break

      case "Enter":
        if (currentHighlightedIndex >= 0) {
          event.preventDefault()
          selectHighlighted()
        }
        break

      case "Escape":
        event.preventDefault()
        resetState()
        break
    }
  })
}
